import React, { useState } from 'react';
import { Brain, CheckCircle, XCircle, RotateCcw } from 'lucide-react';

const DrillQuestions = ({ drillData, onNewDrill }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState(new Set());
  const handleAnswerSelect = (questionIndex, selectedOption) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOption
    }));
    
    // Mark question as completed
    setCompletedQuestions(prev => new Set([...prev, questionIndex]));
  };

  const handleSubmit = () => {
    setShowResults(true);
  };

  const handleNewDrill = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setCompletedQuestions(new Set());
    onNewDrill();
  };

  const isAllAnswered = () => {
    return drillData.drillQuestions.length === Object.keys(selectedAnswers).length;
  };

  const getCorrectCount = () => {
    return drillData.drillQuestions.filter((question, index) => 
      selectedAnswers[index] === question.correctAnswer
    ).length;
  };

  const getButtonClass = (questionIndex, option) => {
    const isCorrect = option === drillData.drillQuestions[questionIndex].correctAnswer;
    const isSelected = selectedAnswers[questionIndex] === option;

    if (showResults) {
      if (isCorrect) {
        return 'bg-green-500 text-white border-green-500';
      } else if (isSelected && !isCorrect) {
        return 'bg-red-500 text-white border-red-500';
      } else {
        return 'bg-gray-100 text-gray-700 border-gray-300';
      }
    } else {
      return isSelected
        ? 'bg-blue-500 text-white border-blue-500'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
    }
  };

  const getQuestionStatus = (index) => {
    const selectedAnswer = selectedAnswers[index];
    const correctAnswer = drillData.drillQuestions[index].correctAnswer;
    
    if (!selectedAnswer) return 'unanswered';
    
    if (showResults) {
      if (selectedAnswer === correctAnswer) {
        return 'correct';
      } else {
        return 'incorrect';
      }
    } else {
      // When not showing results yet, just show as answered
      return 'answered';
    }
  };

  const getProgressPercentage = () => {
    return (Object.keys(selectedAnswers).length / drillData.drillQuestions.length) * 100;
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
            <Brain className="w-4 h-4 text-white" />
          </div>
          <h4 className="font-semibold text-gray-900">AI 練習題</h4>
        </div>
        <button
          onClick={handleNewDrill}
          className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
        >
          <RotateCcw className="w-3 h-3" />
          <span>新練習</span>
        </button>
      </div>

      {/* Progress Section */}
      <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-semibold text-gray-900">練習進度</h5>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-700">
                完成度: {Math.round(getProgressPercentage())}% ({Object.keys(selectedAnswers).length}/{drillData.drillQuestions.length} 正確)
              </span>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-700">
            {Object.keys(selectedAnswers).length}/{drillData.drillQuestions.length} 題
          </span>
        </div>

        {/* Circular Question Status Buttons */}
        <div className="flex justify-center space-x-3">
          {drillData.drillQuestions.map((_, index) => {
            const status = getQuestionStatus(index);
            
            return (
              <div
                key={index}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm transition-all duration-200 ${
                  status === 'correct'
                    ? 'bg-green-500'
                    : status === 'incorrect'
                    ? 'bg-red-500'
                    : status === 'answered'
                    ? 'bg-blue-500'
                    : 'bg-gray-400'
                }`}
              >
                {index + 1}
              </div>
            );
          })}
        </div>
      </div>

      {/* All Questions */}
      <div className="space-y-6">
        {drillData.drillQuestions.map((question, questionIndex) => (
          <div key={questionIndex} className="bg-white rounded-lg p-6 border border-gray-200">
            {/* Question Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-3 py-1 rounded-lg text-sm font-medium">
                  練習題 {questionIndex + 1}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">中級</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">AI</span>
              </div>
            </div>

            {/* Question Text */}
            <div className="mb-6">
              <p className="text-lg text-gray-900 font-medium">{question.question}</p>
            </div>

            {/* Answer Options */}
            <div className="space-y-3 mb-6">
              {question.options.map((option, optionIndex) => (
                <button
                  key={optionIndex}
                  onClick={() => handleAnswerSelect(questionIndex, option)}
                  disabled={showResults}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 ${
                    getButtonClass(questionIndex, option)
                  } ${showResults ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <span className="text-sm font-medium">{option}</span>
                  {showResults && (
                    getButtonClass(questionIndex, option).includes('green') ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : getButtonClass(questionIndex, option).includes('red') ? (
                      <XCircle className="w-4 h-4" />
                    ) : null
                  )}
                </button>
              ))}
            </div>

            {/* Explanation (show when results are displayed) */}
            {showResults && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h6 className="font-semibold text-green-900">
                    詳細解答 (正確答案: {String.fromCharCode(65 + question.options.indexOf(question.correctAnswer))})
                  </h6>
                </div>
                <p className="text-green-800 text-sm">{question.explanation}</p>
                <div className="mt-3 pt-3 border-t border-green-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                      <Brain className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-blue-600">此為 DeepSeek AI 即時生成內容</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Submit Button */}
      {!showResults && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!isAllAnswered()}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              isAllAnswered()
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            提交答案
          </button>
        </div>
      )}

      {/* Results Summary */}
      {showResults && (
        <div className="mt-6 bg-white rounded-lg p-6 border border-gray-200">
          <h5 className="font-semibold text-gray-900 mb-4 text-center">練習結果</h5>
          <div className="grid grid-cols-2 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{getCorrectCount()}</div>
              <div className="text-sm text-gray-600">正確答案</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">{drillData.drillQuestions.length}</div>
              <div className="text-sm text-gray-600">總題數</div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <div className="text-xl font-semibold text-gray-900">
              正確率: {Math.round((getCorrectCount() / drillData.drillQuestions.length) * 100)}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrillQuestions; 